import React from 'react';
import styled from 'styled-components';
import { 
  CheckCircle, 
  Clock, 
  AlertTriangle, 
  XCircle, 
  User, 
  Building2, 
  Home, 
  DollarSign,
  Mail,
  Phone,
  Calendar,
  Shield,
  Star,
  Zap,
  Minus
} from 'lucide-react';

// Status types used throughout the app
export type PropertyStatus = 'AVAILABLE' | 'FULL' | 'MAINTENANCE' | 'CLOSED';
export type UnitStatus = 'AVAILABLE' | 'OCCUPIED' | 'MAINTENANCE' | 'RESERVED' | 'INACTIVE';
export type TenantStatus = 'ACTIVE' | 'INACTIVE' | 'PENDING' | 'BLACKLISTED';
export type LeaseStatus = 'ACTIVE' | 'EXPIRED' | 'TERMINATED';
export type PaymentStatus = 'PAID' | 'UNPAID' | 'PARTIAL' | 'OVERDUE' | 'CANCELLED' | 'REFUNDED';
export type PaymentType = 'RENT' | 'DEPOSIT' | 'UTILITY' | 'MAINTENANCE' | 'LATE_FEE' | 'OTHER';
export type EmploymentStatus = 'EMPLOYED' | 'SELF_EMPLOYED' | 'STUDENT' | 'UNEMPLOYED';
export type ReminderStatus = 'PENDING' | 'SCHEDULED' | 'SENDING' | 'SENT' | 'DELIVERED' | 'FAILED' | 'CANCELLED' | 'PARTIALLY_SENT';
export type AnnouncementStatus = 'DRAFT' | 'SCHEDULED' | 'SENDING' | 'SENT' | 'CANCELLED';
export type MessagePriority = 'LOW' | 'MEDIUM' | 'HIGH' | 'URGENT';
export type DeliveryStatus = 'PENDING' | 'SENT' | 'DELIVERED' | 'FAILED' | 'CANCELLED';

// Union type for all possible statuses
export type StatusType = 
  | PropertyStatus 
  | UnitStatus 
  | TenantStatus 
  | LeaseStatus 
  | PaymentStatus 
  | PaymentType
  | EmploymentStatus 
  | ReminderStatus 
  | AnnouncementStatus 
  | MessagePriority 
  | DeliveryStatus;

// Status configuration with colors and icons
const STATUS_CONFIG: Record<StatusType, { 
  label: string; 
  color: string; 
  bgColor: string; 
  textColor: string;
  borderColor: string;
  icon: React.ComponentType<any>;
}> = {
  // Property Status
  AVAILABLE: { 
    label: 'Available', 
    color: 'hsl(104 98% 38%)', 
    bgColor: 'hsl(104 98% 95%)', 
    textColor: 'hsl(104 98% 25%)', 
    borderColor: 'hsl(104 98% 80%)',
    icon: CheckCircle
  },
  FULL: { 
    label: 'Full', 
    color: 'hsl(217.2 91.2% 59.8%)', 
    bgColor: 'hsl(217.2 91.2% 95%)', 
    textColor: 'hsl(217.2 91.2% 35%)', 
    borderColor: 'hsl(217.2 91.2% 80%)',
    icon: Building2
  },
  MAINTENANCE: { 
    label: 'Maintenance', 
    color: 'hsl(38 92% 60%)', 
    bgColor: 'hsl(38 92% 95%)', 
    textColor: 'hsl(38 92% 35%)', 
    borderColor: 'hsl(38 92% 80%)',
    icon: AlertTriangle
  },
  CLOSED: { 
    label: 'Closed', 
    color: 'hsl(240 5.3% 26.1%)', 
    bgColor: 'hsl(240 5.3% 95%)', 
    textColor: 'hsl(240 5.3% 20%)', 
    borderColor: 'hsl(240 5.3% 80%)',
    icon: XCircle
  },

  // Unit Status
  OCCUPIED: { 
    label: 'Occupied', 
    color: 'hsl(217.2 91.2% 59.8%)', 
    bgColor: 'hsl(217.2 91.2% 95%)', 
    textColor: 'hsl(217.2 91.2% 35%)', 
    borderColor: 'hsl(217.2 91.2% 80%)',
    icon: User
  },
  RESERVED: { 
    label: 'Reserved', 
    color: 'hsl(262 83% 58%)', 
    bgColor: 'hsl(262 83% 95%)', 
    textColor: 'hsl(262 83% 35%)', 
    borderColor: 'hsl(262 83% 80%)',
    icon: Star
  },

  // Tenant Status
  ACTIVE: { 
    label: 'Active', 
    color: 'hsl(104 98% 38%)', 
    bgColor: 'hsl(104 98% 95%)', 
    textColor: 'hsl(104 98% 25%)', 
    borderColor: 'hsl(104 98% 80%)',
    icon: CheckCircle
  },
  INACTIVE: { 
    label: 'Inactive', 
    color: 'hsl(240 5.3% 26.1%)', 
    bgColor: 'hsl(240 5.3% 95%)', 
    textColor: 'hsl(240 5.3% 20%)', 
    borderColor: 'hsl(240 5.3% 80%)',
    icon: Minus
  },
  PENDING: { 
    label: 'Pending', 
    color: 'hsl(262 83% 58%)', 
    bgColor: 'hsl(262 83% 95%)', 
    textColor: 'hsl(262 83% 35%)', 
    borderColor: 'hsl(262 83% 80%)',
    icon: Clock
  },
  BLACKLISTED: { 
    label: 'Blacklisted', 
    color: 'hsl(0 84% 60%)', 
    bgColor: 'hsl(0 84% 95%)', 
    textColor: 'hsl(0 84% 35%)', 
    borderColor: 'hsl(0 84% 80%)',
    icon: XCircle
  },

  // Lease Status
  EXPIRED: { 
    label: 'Expired', 
    color: 'hsl(38 92% 60%)', 
    bgColor: 'hsl(38 92% 95%)', 
    textColor: 'hsl(38 92% 35%)', 
    borderColor: 'hsl(38 92% 80%)',
    icon: Calendar
  },
  TERMINATED: { 
    label: 'Terminated', 
    color: 'hsl(0 84% 60%)', 
    bgColor: 'hsl(0 84% 95%)', 
    textColor: 'hsl(0 84% 35%)', 
    borderColor: 'hsl(0 84% 80%)',
    icon: XCircle
  },

  // Payment Status
  PAID: { 
    label: 'Paid', 
    color: 'hsl(104 98% 38%)', 
    bgColor: 'hsl(104 98% 95%)', 
    textColor: 'hsl(104 98% 25%)', 
    borderColor: 'hsl(104 98% 80%)',
    icon: CheckCircle
  },
  UNPAID: { 
    label: 'Unpaid', 
    color: 'hsl(0 84% 60%)', 
    bgColor: 'hsl(0 84% 95%)', 
    textColor: 'hsl(0 84% 35%)', 
    borderColor: 'hsl(0 84% 80%)',
    icon: XCircle
  },
  PARTIAL: { 
    label: 'Partial', 
    color: 'hsl(38 92% 60%)', 
    bgColor: 'hsl(38 92% 95%)', 
    textColor: 'hsl(38 92% 35%)', 
    borderColor: 'hsl(38 92% 80%)',
    icon: AlertTriangle
  },
  OVERDUE: { 
    label: 'Overdue', 
    color: 'hsl(0 84% 60%)', 
    bgColor: 'hsl(0 84% 95%)', 
    textColor: 'hsl(0 84% 35%)', 
    borderColor: 'hsl(0 84% 80%)',
    icon: AlertTriangle
  },
  CANCELLED: { 
    label: 'Cancelled', 
    color: 'hsl(240 5.3% 26.1%)', 
    bgColor: 'hsl(240 5.3% 95%)', 
    textColor: 'hsl(240 5.3% 20%)', 
    borderColor: 'hsl(240 5.3% 80%)',
    icon: XCircle
  },
  REFUNDED: { 
    label: 'Refunded', 
    color: 'hsl(217.2 91.2% 59.8%)', 
    bgColor: 'hsl(217.2 91.2% 95%)', 
    textColor: 'hsl(217.2 91.2% 35%)', 
    borderColor: 'hsl(217.2 91.2% 80%)',
    icon: CheckCircle
  },

  // Payment Types
  RENT: { 
    label: 'Rent', 
    color: 'hsl(217.2 91.2% 59.8%)', 
    bgColor: 'hsl(217.2 91.2% 95%)', 
    textColor: 'hsl(217.2 91.2% 35%)', 
    borderColor: 'hsl(217.2 91.2% 80%)',
    icon: Home
  },
  DEPOSIT: { 
    label: 'Deposit', 
    color: 'hsl(104 98% 38%)', 
    bgColor: 'hsl(104 98% 95%)', 
    textColor: 'hsl(104 98% 25%)', 
    borderColor: 'hsl(104 98% 80%)',
    icon: Shield
  },
  UTILITY: { 
    label: 'Utility', 
    color: 'hsl(262 83% 58%)', 
    bgColor: 'hsl(262 83% 95%)', 
    textColor: 'hsl(262 83% 35%)', 
    borderColor: 'hsl(262 83% 80%)',
    icon: Zap
  },
  LATE_FEE: { 
    label: 'Late Fee', 
    color: 'hsl(0 84% 60%)', 
    bgColor: 'hsl(0 84% 95%)', 
    textColor: 'hsl(0 84% 35%)', 
    borderColor: 'hsl(0 84% 80%)',
    icon: AlertTriangle
  },
  OTHER: { 
    label: 'Other', 
    color: 'hsl(240 5.3% 26.1%)', 
    bgColor: 'hsl(240 5.3% 95%)', 
    textColor: 'hsl(240 5.3% 20%)', 
    borderColor: 'hsl(240 5.3% 80%)',
    icon: Minus
  },

  // Employment Status
  EMPLOYED: { 
    label: 'Employed', 
    color: 'hsl(104 98% 38%)', 
    bgColor: 'hsl(104 98% 95%)', 
    textColor: 'hsl(104 98% 25%)', 
    borderColor: 'hsl(104 98% 80%)',
    icon: CheckCircle
  },
  SELF_EMPLOYED: { 
    label: 'Self-Employed', 
    color: 'hsl(217.2 91.2% 59.8%)', 
    bgColor: 'hsl(217.2 91.2% 95%)', 
    textColor: 'hsl(217.2 91.2% 35%)', 
    borderColor: 'hsl(217.2 91.2% 80%)',
    icon: User
  },
  STUDENT: { 
    label: 'Student', 
    color: 'hsl(262 83% 58%)', 
    bgColor: 'hsl(262 83% 95%)', 
    textColor: 'hsl(262 83% 35%)', 
    borderColor: 'hsl(262 83% 80%)',
    icon: Star
  },
  UNEMPLOYED: { 
    label: 'Unemployed', 
    color: 'hsl(240 5.3% 26.1%)', 
    bgColor: 'hsl(240 5.3% 95%)', 
    textColor: 'hsl(240 5.3% 20%)', 
    borderColor: 'hsl(240 5.3% 80%)',
    icon: Minus
  },

  // Reminder Status
  SCHEDULED: { 
    label: 'Scheduled', 
    color: 'hsl(38 92% 60%)', 
    bgColor: 'hsl(38 92% 95%)', 
    textColor: 'hsl(38 92% 35%)', 
    borderColor: 'hsl(38 92% 80%)',
    icon: Calendar
  },
  SENDING: { 
    label: 'Sending', 
    color: 'hsl(217.2 91.2% 59.8%)', 
    bgColor: 'hsl(217.2 91.2% 95%)', 
    textColor: 'hsl(217.2 91.2% 35%)', 
    borderColor: 'hsl(217.2 91.2% 80%)',
    icon: Mail
  },
  SENT: { 
    label: 'Sent', 
    color: 'hsl(104 98% 38%)', 
    bgColor: 'hsl(104 98% 95%)', 
    textColor: 'hsl(104 98% 25%)', 
    borderColor: 'hsl(104 98% 80%)',
    icon: CheckCircle
  },
  DELIVERED: { 
    label: 'Delivered', 
    color: 'hsl(78 100% 44%)', 
    bgColor: 'hsl(78 100% 95%)', 
    textColor: 'hsl(78 100% 25%)', 
    borderColor: 'hsl(78 100% 80%)',
    icon: CheckCircle
  },
  FAILED: { 
    label: 'Failed', 
    color: 'hsl(0 84% 60%)', 
    bgColor: 'hsl(0 84% 95%)', 
    textColor: 'hsl(0 84% 35%)', 
    borderColor: 'hsl(0 84% 80%)',
    icon: XCircle
  },
  PARTIALLY_SENT: { 
    label: 'Partially Sent', 
    color: 'hsl(38 92% 60%)', 
    bgColor: 'hsl(38 92% 95%)', 
    textColor: 'hsl(38 92% 35%)', 
    borderColor: 'hsl(38 92% 80%)',
    icon: AlertTriangle
  },

  // Announcement Status
  DRAFT: { 
    label: 'Draft', 
    color: 'hsl(240 5.3% 26.1%)', 
    bgColor: 'hsl(240 5.3% 95%)', 
    textColor: 'hsl(240 5.3% 20%)', 
    borderColor: 'hsl(240 5.3% 80%)',
    icon: Minus
  },

  // Message Priority
  LOW: { 
    label: 'Low', 
    color: 'hsl(104 98% 38%)', 
    bgColor: 'hsl(104 98% 95%)', 
    textColor: 'hsl(104 98% 25%)', 
    borderColor: 'hsl(104 98% 80%)',
    icon: Minus
  },
  MEDIUM: { 
    label: 'Medium', 
    color: 'hsl(48 96% 53%)', 
    bgColor: 'hsl(48 96% 95%)', 
    textColor: 'hsl(48 96% 25%)', 
    borderColor: 'hsl(48 96% 80%)',
    icon: AlertTriangle
  },
  HIGH: { 
    label: 'High', 
    color: 'hsl(38 92% 60%)', 
    bgColor: 'hsl(38 92% 95%)', 
    textColor: 'hsl(38 92% 35%)', 
    borderColor: 'hsl(38 92% 80%)',
    icon: AlertTriangle
  },
  URGENT: { 
    label: 'Urgent', 
    color: 'hsl(0 84% 60%)', 
    bgColor: 'hsl(0 84% 95%)', 
    textColor: 'hsl(0 84% 35%)', 
    borderColor: 'hsl(0 84% 80%)',
    icon: Zap
  },
};

// Styled Components
const PillContainer = styled.span<{ 
  $bgColor: string; 
  $textColor: string; 
  $borderColor: string;
  $size: 'sm' | 'md' | 'lg';
  $variant: 'default' | 'outline' | 'solid';
}>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: ${props => props.$size === 'sm' ? '2px 6px' : props.$size === 'md' ? '3px 8px' : '4px 10px'};
  border-radius: 12px;
  font-size: ${props => props.$size === 'sm' ? '10px' : props.$size === 'md' ? '11px' : '12px'};
  font-weight: 500;
  line-height: 1;
  white-space: nowrap;
  transition: all 0.2s ease;
  cursor: default;
  
  ${props => {
    if (props.$variant === 'outline') {
      return `
        background: transparent;
        color: ${props.$textColor};
        border: 1px solid ${props.$borderColor};
      `;
    } else if (props.$variant === 'solid') {
      return `
        background: ${props.$textColor};
        color: white;
        border: 1px solid ${props.$textColor};
      `;
    } else {
      return `
        background: ${props.$bgColor};
        color: ${props.$textColor};
        border: 1px solid ${props.$borderColor};
      `;
    }
  }}

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }

  svg {
    width: ${props => props.$size === 'sm' ? '10px' : props.$size === 'md' ? '11px' : '12px'};
    height: ${props => props.$size === 'sm' ? '10px' : props.$size === 'md' ? '11px' : '12px'};
  }
`;

const PillText = styled.span`
  text-transform: capitalize;
`;

// Main StatusPill component
interface StatusPillProps {
  status: StatusType;
  variant?: 'default' | 'outline' | 'solid';
  size?: 'sm' | 'md' | 'lg';
  showIcon?: boolean;
  className?: string;
  /** Override label (e.g. show "Pending" for DRAFT invoices in financial tables) */
  displayLabel?: string;
}

export const StatusPill: React.FC<StatusPillProps> = ({
  status,
  variant = 'default',
  size = 'sm',
  showIcon = true,
  className,
  displayLabel
}) => {
  const config = STATUS_CONFIG[status];
  if (!config) {
    console.warn(`Unknown status: ${status}`);
    return null;
  }

  const IconComponent = config.icon;
  const label = displayLabel ?? config.label;

  return (
    <PillContainer
      $bgColor={config.bgColor}
      $textColor={config.textColor}
      $borderColor={config.borderColor}
      $size={size}
      $variant={variant}
      className={className}
    >
      {showIcon && <IconComponent />}
      <PillText>{label}</PillText>
    </PillContainer>
  );
};

// Specialized status pill components for specific types
export const PropertyStatusPill: React.FC<Omit<StatusPillProps, 'status'> & { status: PropertyStatus }> = (props) => (
  <StatusPill {...props} />
);

export const UnitStatusPill: React.FC<Omit<StatusPillProps, 'status'> & { status: UnitStatus }> = (props) => (
  <StatusPill {...props} />
);

export const TenantStatusPill: React.FC<Omit<StatusPillProps, 'status'> & { status: TenantStatus }> = (props) => (
  <StatusPill {...props} />
);

export const PaymentStatusPill: React.FC<Omit<StatusPillProps, 'status'> & { status: PaymentStatus }> = (props) => (
  <StatusPill {...props} />
);

export const ReminderStatusPill: React.FC<Omit<StatusPillProps, 'status'> & { status: ReminderStatus }> = (props) => (
  <StatusPill {...props} />
);

export const MessagePriorityPill: React.FC<Omit<StatusPillProps, 'status'> & { status: MessagePriority }> = (props) => (
  <StatusPill {...props} />
);

export const DeliveryStatusPill: React.FC<Omit<StatusPillProps, 'status'> & { status: DeliveryStatus }> = (props) => (
  <StatusPill {...props} />
);

export const PaymentTypePill: React.FC<Omit<StatusPillProps, 'status'> & { status: PaymentType }> = (props) => (
  <StatusPill {...props} />
);

export const EmploymentStatusPill: React.FC<Omit<StatusPillProps, 'status'> & { status: EmploymentStatus }> = (props) => (
  <StatusPill {...props} />
);

export default StatusPill;
